<template>
  <div class="wrapper p-3">
    <page-header title="Summary Parity Check" />
    <p class="text-muted small mb-3">
      Internal QA tool. Compares <code>/summary/asset/period</code> (new rollup)
      against <code>/summary/aggregated/period</code> (currently used by the app)
      for one asset and one date range. Not linked from any menu.
    </p>

    <data-filter
      :on-apply-filter="onApplyFilter"
      hide-interval
      hide-range-type
      hide-report
    />

    <div v-if="errorMessage" class="alert alert-danger mt-3">{{ errorMessage }}</div>

    <div v-if="isLoading" class="text-center mt-5">
      <wave-loader size="lg" />
    </div>

    <template v-else-if="hasResult">
      <div class="text-muted small mt-3">
        Compared massId <strong>{{ comparedAt.massId }}</strong>
        · {{ comparedAt.startText }} → {{ comparedAt.endText }}
        · range: {{ comparedAt.hourAligned ? 'hour-aligned' : 'NOT hour-aligned' }}
        · new meta.source: <strong>{{ metaSource || '—' }}</strong>
        · serialized size: new {{ formatKb(bytesNew) }} · old {{ formatKb(bytesOld) }}
      </div>

      <div class="text-muted small">
        {{ totalComparedLeaves }} leaf values compared · {{ totalFindings }} finding(s)
      </div>

      <div class="iq-card mt-3" v-for="result in typeResults" :key="result.type">
        <div class="iq-card-header pt-2 d-flex align-items-center justify-content-between">
          <div>
            <h5 class="card-title mb-0 text-uppercase d-inline-block mr-2">{{ result.type }}</h5>
            <span v-if="result.coverageRatio !== null" class="text-muted small">
              coverage: {{ formatRatio(result.coverageRatio) }}
            </span>
          </div>
          <span :class="badgeClass(result)">{{ badgeText(result) }}</span>
        </div>
        <div class="iq-card-body">
          <p v-if="result.status === 'absent'" class="text-muted mb-0">
            Not returned by either endpoint.
          </p>
          <p v-else-if="isNoValuesCompared(result)" class="text-muted mb-0">
            No values compared — both endpoints returned empty data for this type.
          </p>
          <p v-else-if="!result.findings.length" class="text-success mb-0">
            Identical — {{ result.comparedLeaves }} leaf values compared.
          </p>
          <table v-else class="table table-sm table-bordered mb-0">
            <thead>
              <tr>
                <th>Path</th>
                <th>Old</th>
                <th>New</th>
                <th class="text-right">Diff</th>
                <th class="text-right">Δ (new - old)</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(finding, index) in result.findings"
                :key="finding.path + '#' + index"
                :class="rowClass(finding)"
              >
                <td><code>{{ finding.path }}</code></td>
                <td :title="fullValueText(finding.oldValue)">{{ formatValue(finding.oldValue) }}</td>
                <td :title="fullValueText(finding.newValue)">{{ formatValue(finding.newValue) }}</td>
                <td class="text-right">{{ diffText(finding) }}</td>
                <td class="text-right">{{ formatDelta(finding) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if="result.findings.length" class="text-muted small mb-0 mt-2">
            {{ result.comparedLeaves }} leaf values compared.
          </p>
        </div>
      </div>

      <details class="mt-3">
        <summary class="font-weight-bold">Raw response — new (/summary/asset/period)</summary>
        <json-viewer :value="rawNew" :expand-depth="3" copyable boxed class="mt-2" />
      </details>

      <details class="mt-2">
        <summary class="font-weight-bold">Raw response — old (/summary/aggregated/period)</summary>
        <json-viewer :value="rawOld" :expand-depth="1" copyable boxed class="mt-2" />
      </details>
    </template>

    <div v-else-if="!errorMessage" class="text-muted mt-4">
      Pick an asset and a date range, then click Apply.
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { mapGetters } from 'vuex';
import { formatNum } from '@/util/number';
import DataFilter from '../../components/DataFilter';
import SummarySvc from '../../services/SummarySvc';
import { deepDiff } from '../../util/summary/summaryDiff';

const DEVC_TYPES = ['gps', 'rpm', 'flowmeter', 'ae'];

export default {
  name: 'SummaryParityCheck',
  components: {
    DataFilter,
  },
  data() {
    return {
      isLoading: false,
      errorMessage: '',
      rawNew: null,
      rawOld: null,
      bytesNew: 0,
      bytesOld: 0,
      // Apa yang SEDANG ditampilin - belum tentu sama sama isi filter
      // sekarang, karena filter bisa diubah tanpa diklik Apply.
      comparedAt: null,
    };
  },
  computed: {
    ...mapGetters('filter', ['currentMassId']),
    hasResult() {
      return !!(this.rawNew && this.rawOld);
    },
    // Satu entri per tipe device. `status` beda-bedain tiga keadaan yang
    // gampang ketuker: beneran cocok, cuma ada sebelah, dan gak ada di
    // dua-duanya. Nol temuan karena gak ada data itu beda artinya sama nol
    // temuan karena semuanya cocok.
    typeResults() {
      if (!this.hasResult) return [];

      return DEVC_TYPES.map((type) => {
        const summaryNew = this.rawNew.data && this.rawNew.data[type]
          ? this.rawNew.data[type].summary : null;
        const summaryOld = this.rawOld.data && this.rawOld.data[type]
          ? this.rawOld.data[type].summary : null;
        const coverageRatio = this.coverageRatioFor(type);

        if (!summaryNew && !summaryOld) {
          return {
            type, status: 'absent', findings: [], comparedLeaves: 0, coverageRatio,
          };
        }

        if (!summaryNew || !summaryOld) {
          return {
            type,
            status: 'missing',
            comparedLeaves: 0,
            coverageRatio,
            findings: [{
              path: type,
              kind: summaryNew ? 'onlyInNew' : 'onlyInOld',
              newValue: summaryNew || undefined,
              oldValue: summaryOld || undefined,
            }],
          };
        }

        const { findings, comparedLeaves } = deepDiff(summaryNew, summaryOld, type);
        return {
          type, status: 'compared', findings, comparedLeaves, coverageRatio,
        };
      });
    },
    totalFindings() {
      return this.typeResults.reduce((sum, r) => sum + r.findings.length, 0);
    },
    totalComparedLeaves() {
      return this.typeResults.reduce((sum, r) => sum + r.comparedLeaves, 0);
    },
    // meta ada di sisi respons baru aja (endpoint lama gak punya konsep ini).
    metaSource() {
      return this.rawNew && this.rawNew.meta ? this.rawNew.meta.source : null;
    },
  },
  async mounted() {
    // Halaman ini cuma kejangkau lewat URL langsung, jadi seringnya justru
    // halaman PERTAMA di sesi - daftar aset di Vuex masih kosong kalau gak
    // diisi di sini.
    // Sengaja TIDAK pakai initAssetSummary: helper itu ikut nembak
    // /summary/aggregated/period pas mount, padahal halaman ini justru ada
    // buat ngamatin request kedua endpoint dengan bersih.
    const { $store } = this;
    try {
      if (!($store.getters['asset/assets'] || []).length) {
        await $store.dispatch('asset/getAssetsData');
      }
      const assets = $store.getters['asset/assets'] || [];
      if (!this.currentMassId && assets.length) {
        await $store.dispatch('filter/setCurrentMassId', assets[0].massId);
      }
    } catch (err) {
      this.errorMessage = 'Failed to load asset list.';
    }
  },
  methods: {
    isCompoundValue(value) {
      return value !== null && typeof value === 'object';
    },
    formatValue(value) {
      if (value === undefined) return '—';
      if (value === null) return 'null';
      if (typeof value === 'object') {
        const text = JSON.stringify(value);
        return text.length > 120 ? `${text.slice(0, 120)}… (truncated)` : text;
      }
      return String(value);
    },
    // Isi lengkap buat atribut `title` - dari sini nilai yang kepotong di
    // formatValue tetep kebaca lewat hover.
    fullValueText(value) {
      if (value === undefined) return '';
      if (value === null) return 'null';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    },
    formatPct(pct) {
      return pct === null || pct === undefined ? '—' : `${formatNum(pct, 2)}%`;
    },
    // Objek/array dibandingin utuh (lewat JSON.stringify di deepDiff), jadi
    // gak ada angka persen yang masuk akal buat ditampilin.
    diffText(finding) {
      if (finding.kind === 'onlyInNew') return 'only in new';
      if (finding.kind === 'onlyInOld') return 'only in old';
      if (this.isCompoundValue(finding.newValue) || this.isCompoundValue(finding.oldValue)) {
        return 'compared as a whole';
      }
      return this.formatPct(finding.pct);
    },
    formatDelta(finding) {
      if (finding.kind !== 'value') return '—';
      const { newValue, oldValue } = finding;
      if (typeof newValue !== 'number' || typeof oldValue !== 'number') return '—';
      if (Number.isNaN(newValue) || Number.isNaN(oldValue)) return '—';
      const delta = Math.round((newValue - oldValue) * 1e6) / 1e6;
      return delta > 0 ? `+${delta}` : `${delta}`;
    },
    rowClass(finding) {
      if (finding.severity === 'major') return 'table-danger';
      if (finding.severity === 'minor') return 'table-warning';
      return '';
    },
    // Nol daun terbanding itu bukan "cocok" - itu artinya gak ada apa-apa
    // yang dibandingin (mis. aset gak punya device tipe ini di dua-duanya).
    isNoValuesCompared(result) {
      return result.status === 'compared' && result.comparedLeaves === 0 && !result.findings.length;
    },
    badgeClass(result) {
      if (result.status === 'absent') return 'badge badge-secondary';
      if (this.isNoValuesCompared(result)) return 'badge badge-secondary';
      return result.findings.length ? 'badge badge-danger' : 'badge badge-success';
    },
    badgeText(result) {
      if (result.status === 'absent') return 'NO DATA';
      if (result.status === 'missing') return 'MISSING ON ONE SIDE';
      if (this.isNoValuesCompared(result)) return 'NO VALUES COMPARED';
      return result.findings.length ? `DIFF (${result.findings.length})` : 'PARITY';
    },
    formatKb(bytes) {
      return `${formatNum((bytes / 1024), 1)} KB`;
    },
    formatRatio(ratio) {
      return ratio === null || ratio === undefined ? '—' : `${formatNum((ratio * 100), 1)}%`;
    },
    coverageRatioFor(type) {
      const coverage = this.rawNew && this.rawNew.meta && this.rawNew.meta.coverage;
      return coverage && coverage[type] ? coverage[type].ratio : null;
    },
    describeFailure(endpoint, error) {
      const status = error && error.response ? error.response.status : null;
      return `${endpoint} failed: ${status ? `HTTP ${status}` : 'network error'}`;
    },
    async onApplyFilter() {
      this.isLoading = true;
      this.errorMessage = '';
      // Hasil lama dibuang duluan supaya gak kebaca sebagai hasil baru.
      this.rawNew = null;
      this.rawOld = null;
      this.comparedAt = null;

      try {
        await this.fetchBoth();
      } finally {
        this.isLoading = false;
      }
    },
    async fetchBoth() {
      const devcMassId = this.currentMassId;
      if (!devcMassId) {
        this.errorMessage = 'No asset selected.';
        return;
      }

      const { start, end } = this.$store.getters['filter/date'];
      const startUnix = moment(start).unix();
      const endUnix = moment(end).unix();

      const [resNew, resOld] = await Promise.allSettled([
        SummarySvc.getAssetSummaryRollup({
          devcType: DEVC_TYPES,
          devcMassId,
          start: startUnix,
          end: endUnix,
        }),
        SummarySvc.getSummary({
          devcType: DEVC_TYPES,
          devcMassId,
          start: startUnix,
          end: endUnix,
          aggregatedUnit: 'HOUR',
          aggregatedLength: 1,
          showDetails: true,
        }),
      ]);

      // Request yang dibatalin (pindah halaman / Apply dobel) bukan error.
      const reasons = [resNew, resOld]
        .filter((r) => r.status === 'rejected')
        .map((r) => r.reason);
      if (reasons.some((e) => axios.isCancel(e))) return;

      const failures = [];
      if (resNew.status === 'rejected') {
        failures.push(this.describeFailure('/summary/asset/period', resNew.reason));
      }
      if (resOld.status === 'rejected') {
        failures.push(this.describeFailure('/summary/aggregated/period', resOld.reason));
      }
      // Diff separuh jadi lebih nyesatin daripada gak ada hasil sama sekali.
      if (failures.length) {
        this.errorMessage = failures.join(' | ');
        return;
      }

      const bodyNew = resNew.value.data;
      const bodyOld = resOld.value.data;
      // HTTP 200 dengan body kosong bukan "gak ada hasil" - itu respons yang
      // gak bisa dipakai, dan harus keliatan beda dari keadaan "belum Apply".
      if (!bodyNew || !bodyNew.data) {
        this.errorMessage = '/summary/asset/period returned an empty response body.';
        return;
      }
      if (!bodyOld || !bodyOld.data) {
        this.errorMessage = '/summary/aggregated/period returned an empty response body.';
        return;
      }

      this.bytesNew = JSON.stringify(bodyNew).length;
      this.bytesOld = JSON.stringify(bodyOld).length;
      // Kedua body gak pernah dimutasi setelah ini - freeze biar Vue 2 gak
      // ngobservasi seluruh pohon (~800 KB di sisi lama).
      this.rawNew = Object.freeze(bodyNew);
      this.rawOld = Object.freeze(bodyOld);
      this.comparedAt = {
        massId: devcMassId,
        startText: moment(start).format('DD-MM-YYYY HH:mm'),
        endText: moment(end).format('DD-MM-YYYY HH:mm'),
        hourAligned: startUnix % 3600 === 0 && endUnix % 3600 === 0,
      };
    },
  },
};
</script>
